"use client";

/**
 * useScrollDepth — fires `landing_scroll_depth` once per threshold per tab
 * session at the 25 / 50 / 75 / 100 percent scroll marks.
 *
 * Wiring contract:
 *   - Mount exactly once at the landing root (`LandingV2Client`). The hook
 *     attaches a passive scroll listener on `window` and computes
 *     `scrollTop / (scrollHeight - clientHeight)` rounded to integer %.
 *   - rAF-throttled: each scroll event schedules a single rAF callback;
 *     subsequent scrolls within the same frame coalesce. Avoids pulling in
 *     lodash.throttle for one usage.
 *   - sessionStorage-scoped dedupe via `trackEventSessionOnce` — survives
 *     Fast Refresh + StrictMode double-effect-fire + back/forward in tab.
 *
 * Why a hook (not a layout-level effect):
 *   - Landing-specific event taxonomy lives next to landing scenes. The
 *     listener cost is one passive scroll handler; cleanup is trivial.
 *
 * Why no library:
 *   - The implementation is ~25 lines. `usehooks-ts` does NOT ship a
 *     threshold-based scroll-depth hook out of the box; closest fit is
 *     `useScroll` which returns position, not threshold-crossing events.
 *   - Adding a dep solely for rAF throttling is overkill — rAF is native.
 *
 * SSR-safety:
 *   - `useEffect` only runs client-side.
 *
 * Edge cases:
 *   - If `scrollHeight <= clientHeight` (page shorter than viewport, which is
 *     impossible on landing-v2 but we guard anyway), the depth calculation is
 *     NaN. We skip in that case rather than firing 100% on every scroll.
 *   - The 100% bucket fires when depth >= 95% (browsers don't always reach
 *     exactly 100 due to rubber-band / fractional pixel math).
 *
 * Anti-pattern compliance:
 *   - No module-top-level const for control-flow. The thresholds array is a
 *     literal inside the consumer function (it's pure data — a number list —
 *     but kept inside to be safe).
 */

import { useEffect } from "react";
import { trackEventSessionOnce } from "@/lib/analytics";

export function useScrollDepth(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Thresholds + their fired-state. Local to the effect so re-mount resets
    // the per-mount latch, but actual cross-session dedupe is owned by
    // `trackEventSessionOnce` (sessionStorage).
    const thresholds: ReadonlyArray<25 | 50 | 75 | 100> = [25, 50, 75, 100];
    const localFired = new Set<number>();
    let rafScheduled = false;

    const compute = () => {
      rafScheduled = false;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const scrollHeight = doc.scrollHeight;
      const clientHeight = doc.clientHeight || window.innerHeight;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      const pct = (scrollTop / maxScroll) * 100;

      for (const t of thresholds) {
        if (localFired.has(t)) continue;
        // 100% bucket eases on fractional pixel math at page bottom.
        const hit = t === 100 ? pct >= 95 : pct >= t;
        if (hit) {
          localFired.add(t);
          trackEventSessionOnce(
            "landing_scroll_depth",
            `pct-${t}`,
            { pct: t },
          );
        }
      }
    };

    const onScroll = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      window.requestAnimationFrame(compute);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // Fire once on mount in case the page loads scrolled (anchor link, restore).
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
}
